import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  History, Search, X, Filter,
  ChevronDown, ChevronUp, FileText,
  LayoutGrid, Layers,
  CheckCircle, XCircle,
  Hash, User, Calendar, Tag, Package, GitCompare, FileJson
} from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, exactColumnRule, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { CustomDatePicker, CustomSelect } from '../../shared/components/custom';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { shouldShowCardsToolbarSearch } from '../../shared/utils/listPageSearchVisibility';
import { useTranslation } from 'react-i18next';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import FacturationListPageLayout, {
  facturationTableScrollClass,
} from '../facturation/components/FacturationListPageLayout';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import {
  countArticleActive,
  countArticleInactive,
  isArticleActiveStatus,
  matchesArticleStatusFilter,
  getArticleStatusLabel,
} from './articlesStatusUtils';
import {
  buildArticleStatusBadgeOptions,
  buildArticleStatusColumnFilterOptions,
  buildArticleStatusSelectOptions,
  getCatalogStatusBadgeStyle,
} from './articleCatalogStatusOptions';
import {buildBulkDeleteFilterAction,
  buildArticlesStatusFilterOptions,
  articlesStatusFilterLabel,
  createArticleStatusChangeHandler,
  QAWAEM_TABLE_ACTIONS_TH_CLASS,
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemColumnVisibility, QawaemTableStickyActionsTd, ArticlesConfigNotice, sliceArticlesListItems } from './articlesListPageUtils';
import { useArticlesConfig, useArticlesListViewMode } from './useArticlesConfig';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import '../../shared/styles/GPSTrackingDatePicker.css';
import { AppConfirmDialog } from '../../shared/components/AppConfirmDialog';
import { useCrudDeleteConfirm } from '../../shared/hooks/useCrudDeleteConfirm';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { generateEntityReference, getEntityPrefix } from '../../shared/utils/generateEntityReference';
import { useSettings } from '../../core/contexts/SettingsContext';

const NS = 'modules.article.pages.changelogPage';

const ENTITY_TYPE_VALUES = ['product', 'service', 'variant', 'price_list', 'cost_method'];
const CHANGE_TYPE_VALUES = ['create', 'update', 'delete'];
const COMMON_CHANGELOG_FIELDS = [
  'السعر',
  'الاسم',
  'ضريبة القيمة المضافة',
  'الوحدة',
  'الحالة',
  'الوصف',
  'SKU',
  'الباركود',
  'التكلفة',
];

const todayDateString = () => new Date().toISOString().split('T')[0];

const ArticleChangelogPage = () => {
  const { t } = useTranslation();
  const { isDashboardLightMode } = useGlobalTheme();
  const { settings } = useSettings();
  const p = (key, opts) => t(`${NS}.${key}`, opts);
  const qawaem = isSaudiBrand();
  const articlesConfig = useArticlesConfig();

  const entityTypeOptions = ENTITY_TYPE_VALUES.map((value) => ({
    value,
    label: p(`entity_${value}`),
    isDefault: true,
  }));

  const changeTypeOptions = CHANGE_TYPE_VALUES.map((value) => ({
    value,
    label: p(`change_${value}`),
    isDefault: true,
  }));

  const statusOptions = useMemo(() => buildArticleStatusSelectOptions(t), [t]);

  const STATUS_OPTIONS = useMemo(() => buildArticleStatusBadgeOptions(t), [t]);

  const statusColumnFilterOptions = useMemo(() => buildArticleStatusColumnFilterOptions(t), [t]);

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [viewMode, setViewMode] = useArticlesListViewMode(articlesConfig);

  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_entries');
  const [quickFilter, setQuickFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const { dateRange, setDateRange } = useSaudiListDateRange();

  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const {
    deleteConfirm,
    closeDeleteConfirm,
    openDeleteOne,
    openBulkDelete,
    takePendingIds,
  } = useCrudDeleteConfirm();
  const entityLabel = p('entity_label', { defaultValue: 'سجل التغيير' });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: 'سجل' });

  const [formTab, setFormTab] = useState('entity');

  const [formData, setFormData] = useState({
    change_number: '',
    entity_type: '',
    entity_id: '',
    entity_name: '',
    field_name: '',
    old_value: '',
    new_value: '',
    change_type: '',
    user_name: '',
    notes: '',
    changed_at: '',
    status: 'actif',
  });

  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  const getEntityTypeLabel = (type) => {
    const match = entityTypeOptions.find((opt) => opt.value === type);
    return match?.label || type || '—';
  };

  const getChangeTypeLabel = (type) => {
    const match = changeTypeOptions.find((opt) => opt.value === type);
    return match?.label || type || '—';
  };

  const isToday = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    const now = new Date();
    return d.getFullYear() === now.getFullYear()
      && d.getMonth() === now.getMonth()
      && d.getDate() === now.getDate();
  };

  useEffect(() => {
    loadEntries().catch((error) => {
      console.error('Error loading changelog entries:', error);
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadEntries = async () => {
    try {
      setLoading(true);
      const response = await localApiAdapter.get('/api/articles/changelog');
      const data = response?.data ?? response;
      setEntries(sortRecordsNewestFirst(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error('Error loading changelog entries:', error);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = useCallback(
    createArticleStatusChangeHandler('/api/articles/changelog', loadEntries),
    [],
  );

  const generateChangeNumber = useCallback(() => {
    const prefix = getEntityPrefix(settings, 'changelog', 'CHG');
    return generateEntityReference(prefix, entries, (r) => r.change_number);
  }, [entries, settings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.entity_type.trim() || !formData.field_name.trim() || !formData.status.trim()) {
      window.alert(p('alert_required'));
      return;
    }
    try {
      setLoading(true);
      const changeNumber = (formData.change_number || '').trim() || generateChangeNumber();
      const payload = {
        change_number: changeNumber,
        entity_type: formData.entity_type.trim(),
        entity_id: formData.entity_id || null,
        entity_name: formData.entity_name || null,
        field_name: formData.field_name.trim(),
        old_value: formData.old_value || null,
        new_value: formData.new_value || null,
        change_type: formData.change_type || null,
        user_name: formData.user_name || null,
        notes: formData.notes || null,
        changed_at: formData.changed_at || new Date().toISOString(),
        status: formData.status.trim(),
      };
      if (selectedRecord) {
        await localApiAdapter.put(`/api/articles/changelog/${selectedRecord.id}`, payload);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/api/articles/changelog', payload);
        notifyCreated(showNotify, entityLabel);
      }
      await loadEntries();
      resetForm();
    } catch (error) {
      console.error('Error saving changelog entry:', error);
      notifyError(showNotify, error?.response?.data?.error || p('alert_save_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setSelectedRecord(record);
    setFormData({
      change_number: record.change_number ?? '',
      entity_type: record.entity_type ?? '',
      entity_id: record.entity_id ?? '',
      entity_name: record.entity_name ?? '',
      field_name: record.field_name ?? '',
      old_value: record.old_value ?? '',
      new_value: record.new_value ?? '',
      change_type: record.change_type ?? '',
      user_name: record.user_name ?? '',
      notes: record.notes ?? '',
      changed_at: record.changed_at ?? '',
      status: record.status ?? '',
    });
    setFormTab('entity');
    setShowModal(true);
  };

  const openNewEntryModal = () => {
    setSelectedRecord(null);
    setFormData({
      change_number: generateChangeNumber(),
      entity_type: '',
      entity_id: '',
      entity_name: '',
      field_name: '',
      old_value: '',
      new_value: '',
      change_type: '',
      user_name: '',
      notes: '',
      changed_at: todayDateString(),
      status: 'actif',
    });
    setFormTab('entity');
    setShowModal(true);
  };

  

  const handleDelete = (id) => openDeleteOne(id);

  const handleBulkDelete = () => {
    if (!selectedRecords.length) return;
    openBulkDelete(selectedRecords);
  };

  const executeDeleteConfirmed = async () => {
    const ids = takePendingIds();
    if (!ids.length) return;
    try {
      if (ids.length === 1) {
        setLoading(true);
        await localApiAdapter.delete(`/api/articles/changelog/${ids[0]}`);
        notifyDeleted(showNotify, entityLabel);
        await loadEntries();
      } else {
        const deleteCount = ids.length;
        await Promise.all(ids.map((id) => localApiAdapter.delete(`/api/articles/changelog/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
        setSelectedRecords([]);
        loadEntries();
      }
    } catch (error) {
      console.error('Error deleting changelog entry:', error);
        notifyError(showNotify, error?.response?.data?.error || p('alert_save_error'));
      } finally {
        setLoading(false);
    }
  };

  const handleView = (record) => {
    setSelectedRecord(record);
    setShowViewModal(true);
  };

  const resetForm = () => {
    setFormData({
      change_number: '',
      entity_type: '',
      entity_id: '',
      entity_name: '',
      field_name: '',
      old_value: '',
      new_value: '',
      change_type: '',
      user_name: '',
      notes: '',
      changed_at: '',
      status: 'actif',
    });
    setSelectedRecord(null);
    setFormTab('entity');
    setShowModal(false);
  };

  const formTabs = useMemo(() => ([
    { id: 'entity', label: p('section_entity'), icon: Package },
    { id: 'meta', label: p('section_meta'), icon: User },
  ]), [p]);

  const handleSelectRecord = (id) => {
    setSelectedRecords(
      selectedRecords.includes(id)
        ? selectedRecords.filter((rId) => rId !== id)
        : [...selectedRecords, id]
    );
  };

  

  const entriesArray = Array.isArray(entries) ? entries : [];

  const fieldNameOptions = useMemo(() => {
    const fromData = [...new Set(entriesArray.map((e) => e.field_name).filter(Boolean))];
    const merged = [...new Set([...fromData, ...COMMON_CHANGELOG_FIELDS])];
    return merged.map((value) => ({
      value,
      label: value,
      isDefault: fromData.includes(value),
    }));
  }, [entriesArray]);

  const userNameOptions = useMemo(() => {
    const names = [...new Set(entriesArray.map((e) => e.user_name).filter(Boolean))];
    return names.map((value) => ({ value, label: value, isDefault: true }));
  }, [entriesArray]);

  const uniqueEntities = new Set(
    entriesArray.map((e) => `${e.entity_type || ''}:${e.entity_id || e.entity_name || ''}`).filter((k) => k !== ':')
  );

  const stats = {
    total: entriesArray.length,
    actifs: countArticleActive(entriesArray),
    inactifs: countArticleInactive(entriesArray),
    entites: uniqueEntities.size,
    aujourdhui: entriesArray.filter((e) => isToday(e.changed_at)).length,
  };

  const changelogDistribution = {
    all_entries: {
      title: p('dist_overview'),
      data: [
        { name: p('filter_active'), count: stats.actifs, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { name: p('filter_inactive'), count: stats.inactifs, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { name: p('stat_today'), count: stats.aujourdhui, color: 'bg-gradient-to-r from-amber-500 to-amber-600' },
        { name: p('stat_entities'), count: stats.entites, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
      ],
    },
    by_entity_type: {
      title: p('col_entity'),
      data: entityTypeOptions.map((opt, i) => ({
        name: opt.label,
        count: entriesArray.filter((e) => e.entity_type === opt.value).length,
        color: [
          'bg-gradient-to-r from-blue-500 to-blue-600',
          'bg-gradient-to-r from-purple-500 to-purple-600',
          'bg-gradient-to-r from-cyan-500 to-cyan-600',
          'bg-gradient-to-r from-indigo-500 to-indigo-600',
          'bg-gradient-to-r from-violet-500 to-violet-600',
        ][i],
        filterKey: opt.value,
      })),
    },
    by_change_type: {
      title: p('col_change'),
      data: changeTypeOptions.map((opt, i) => ({
        name: opt.label,
        count: entriesArray.filter((e) => e.change_type === opt.value).length,
        color: [
          'bg-gradient-to-r from-green-500 to-green-600',
          'bg-gradient-to-r from-blue-500 to-blue-600',
          'bg-gradient-to-r from-red-500 to-red-600',
        ][i],
        filterKey: opt.value,
      })),
    },
    by_status: {
      title: p('col_status'),
      data: [
        { name: p('filter_active'), count: stats.actifs, color: 'bg-gradient-to-r from-green-500 to-green-600', filterKey: 'actif' },
        { name: p('filter_inactive'), count: stats.inactifs, color: 'bg-gradient-to-r from-red-500 to-red-600', filterKey: 'inactif' },
      ],
    },
  };

  const distributionInfo = changelogDistribution[mainViewMode] || changelogDistribution.all_entries;

  const mainTabs = [
    { id: 'all_entries', label: t('common.general_view'), icon: LayoutGrid },
    { id: 'by_entity_type', label: p('col_entity'), icon: Package },
    { id: 'by_change_type', label: p('col_change'), icon: GitCompare },
    { id: 'by_status', label: t('common.by_status'), icon: Tag },
  ];

  const quickFilterButtons = [
    { id: 'all', label: p('filter_all'), icon: Layers },
    ...entityTypeOptions.map((opt) => ({ id: opt.value, label: opt.label, icon: Package })),
    ...changeTypeOptions.map((opt) => ({ id: opt.value, label: opt.label, icon: GitCompare })),
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleString('ar-SA');
    } catch {
      return dateStr;
    }
  };

  const baseFilteredEntries = useMemo(
    () =>
      entriesArray.filter((record) => {
        const matchesSearch =
          !searchTerm ||
          record.change_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.entity_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.field_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.entity_type?.toLowerCase().includes(searchTerm.toLowerCase());

        if (!matchesSearch) return false;

        if (!matchesArticleStatusFilter(record.status, statusFilter)) return false;

        if (dateRange.start || dateRange.end) {
          if (!record.changed_at) return false;
          const changedDate = new Date(record.changed_at);
          if (dateRange.start) {
            const startOfDay = new Date(dateRange.start);
            startOfDay.setHours(0, 0, 0, 0);
            if (changedDate < startOfDay) return false;
          }
          if (dateRange.end) {
            const endOfDay = new Date(dateRange.end);
            endOfDay.setHours(23, 59, 59, 999);
            if (changedDate > endOfDay) return false;
          }
        }

        if (quickFilter === 'all') return true;
        if (ENTITY_TYPE_VALUES.includes(quickFilter)) return record.entity_type === quickFilter;
        if (CHANGE_TYPE_VALUES.includes(quickFilter)) return record.change_type === quickFilter;
        return true;
      }),
    [entriesArray, searchTerm, statusFilter, dateRange, quickFilter],
  );

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('change_number', (r) => r.change_number || ''),
      textColumnRule('entity_name', (r) => [getEntityTypeLabel(r.entity_type), r.entity_name, r.entity_id].filter(Boolean).join(' ')),
      textColumnRule('field_name', (r) => r.field_name || ''),
      textColumnRule('user_name', (r) => r.user_name || ''),
      textColumnRule('changed_at', (r) => formatDate(r.changed_at)),
      textColumnRule('change_type', (r) => [getChangeTypeLabel(r.change_type), r.old_value, r.new_value].filter(Boolean).join(' ')),
      exactColumnRule('status', (r) => r.status || ''),
    ],
    [t, entityTypeOptions, changeTypeOptions],
  );

  const filteredEntries = useMemo(
    () => applyColumnFilters(baseFilteredEntries, colFilters, columnFilterRules),
    [baseFilteredEntries, colFilters, columnFilterRules],
  );

  const filteredEntryIds = useMemo(() => filteredEntries.map((r) => r.id), [filteredEntries]);
  const displayedEntries = useMemo(
    () => sliceArticlesListItems(filteredEntries, articlesConfig.itemsPerPage),
    [filteredEntries, articlesConfig.itemsPerPage],
  );
  const allRowsSelected = filteredEntryIds.length > 0 && filteredEntryIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredEntryIds);
    }
  };

  const getExportData = () =>
    selectedRecords.length > 0
      ? entriesArray.filter((e) => selectedRecords.includes(e.id))
      : filteredEntries;

  const handleExportCSV = () => {
    exportToCSV(getExportData(), `article_changelog_${new Date().toISOString().split('T')[0]}.csv`);
    setShowExportDropdown(false);
  };

  const handleExportExcel = () => {
    exportToExcel(getExportData(), `article_changelog_${new Date().toISOString().split('T')[0]}.xlsx`);
    setShowExportDropdown(false);
  };

  const handleExportJSON = () => {
    const blob = new Blob([JSON.stringify(getExportData(), null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `article_changelog_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };

  const handleImportCSV = () => {
    triggerFileInput(async (file) => {
      try {
        const imported = await importFromFile(file);
        if (!Array.isArray(imported)) {
          window.alert(p('alert_save_error'));
          return;
        }
        for (const item of imported) {
          await localApiAdapter.post('/api/articles/changelog', item);
        }
        loadEntries();
        setShowImportDropdown(false);
      } catch (error) {
        console.error('Import error:', error);
        window.alert(p('alert_save_error'));
      }
    }, '.csv');
  };

  const handleImportExcel = () => {
    triggerFileInput(async (file) => {
      try {
        const imported = await importFromFile(file);
        const rows = Array.isArray(imported) ? imported : [imported];
        for (const item of rows) {
          await localApiAdapter.post('/api/articles/changelog', item);
        }
        loadEntries();
        setShowImportDropdown(false);
      } catch (error) {
        console.error('Import error:', error);
        window.alert(p('alert_save_error'));
      }
    }, '.xlsx,.xls');
  };

  const handleImportJSON = () => {
    triggerFileInput(async (file) => {
      try {
        const content = await file.text();
        const data = JSON.parse(content);
        const rows = Array.isArray(data) ? data : [data];
        for (const item of rows) {
          await localApiAdapter.post('/api/articles/changelog', item);
        }
        loadEntries();
        setShowImportDropdown(false);
      } catch (error) {
        console.error('Import error:', error);
        window.alert(p('alert_save_error'));
      }
    }, '.json');
  };

  const qawaemStatusOptions = useMemo(
    () => buildArticlesStatusFilterOptions(p, t),
    [t],
  );

  const qawaemStatusLabel = articlesStatusFilterLabel(statusFilter, qawaemStatusOptions);

  const { columnVisibility, handleToggleColumn, showCol, visibleDataColCount } = useQawaemColumnVisibility([
    'change_number', 'entity_name', 'field_name', 'change_type', 'user_name', 'changed_at', 'status',
  ]);

  const qawaemChangelogColumns = useMemo(
    () => [
      { key: 'change_number', label: p('col_change_number'), checked: columnVisibility.change_number !== false, required: true },
      { key: 'entity_name', label: p('col_entity'), checked: columnVisibility.entity_name !== false },
      { key: 'field_name', label: p('col_field'), checked: columnVisibility.field_name !== false },
      { key: 'change_type', label: p('col_change'), checked: columnVisibility.change_type !== false },
      { key: 'user_name', label: p('col_user'), checked: columnVisibility.user_name !== false },
      { key: 'changed_at', label: p('col_date'), checked: columnVisibility.changed_at !== false },
      { key: 'status', label: p('col_status'), checked: columnVisibility.status !== false },
      { key: 'actions', label: p('col_actions'), checked: true, required: true },
    ],
    [p, columnVisibility],
  );
  const qawaemVisibleChangelogColumnCount = qawaemChangelogColumns.filter((c) => c.checked).length;
  const tableColSpan = 1 + visibleDataColCount + 1;

  const listStats = useMemo(
    () => [
      { key: 'total', label: p('stat_total'), value: stats.total, icon: History, variant: 'copper' },
      { key: 'active', label: p('stat_active'), value: stats.actifs, icon: CheckCircle, variant: 'forest' },
      { key: 'inactive', label: p('stat_inactive'), value: stats.inactifs, icon: XCircle, variant: 'copper' },
      { key: 'entities', label: p('stat_entities'), value: stats.entites, icon: Package, variant: 'forest' },
    ],
    [stats, p],
  );

  const periodLabels = useMemo(
    () => ({
      label: p('period'),
      startLabel: p('date_start'),
      endLabel: p('date_end'),
      resetLabel: p('reset'),
      applyLabel: p('apply'),
      selectPlaceholder: p('select'),
    }),
    [t],
  );

  const advancedOptionsContent = (
    <div className="space-y-4 pt-4">
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {mainTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); }}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                mainViewMode === tab.id
                  ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              <span className="hidden sm:inline whitespace-nowrap">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {quickFilterButtons.map((btn) => (
            <button
              key={btn.id}
              type="button"
              onClick={() => setQuickFilter(btn.id)}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                quickFilter === btn.id
                  ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
            >
              <btn.icon className="h-5 w-5" />
              <span className="hidden sm:inline whitespace-nowrap">{btn.label}</span>
            </button>
          ))}
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
      >
        <h3 className="text-xl font-semibold text-white mb-4">{distributionInfo.title}</h3>
        <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>
          {distributionInfo.data.map((item, index) => {
            const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
            const percentage = total > 0 ? Math.round((item.count / total) * 100) : 0;
            return (
              <div key={index} className="bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="flex justify-between items-center mb-2">
                  <span className="block w-full text-white/70 text-sm font-medium text-right">{item.name}</span>
                  <span className="text-white font-semibold">{item.count} ({percentage}%)</span>
                </div>
                <div className="h-2.5 bg-white/5 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${percentage}%` }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className={`h-full ${item.color} rounded-full`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );

  const metaLabelClass = qawaem || isDashboardLightMode
    ? 'text-xs font-medium text-[#6e9b6e]'
    : 'text-xs font-medium text-white/55';

  const entityTypeTextClass = {
    product: qawaem || isDashboardLightMode ? 'text-[#5a855a]' : 'text-blue-300',
    service: qawaem || isDashboardLightMode ? 'text-[#7a5a9b]' : 'text-purple-300',
    variant: qawaem || isDashboardLightMode ? 'text-[#6e9b6e]' : 'text-cyan-300',
    price_list: qawaem || isDashboardLightMode ? 'text-[#7a6b4a]' : 'text-indigo-300',
    cost_method: qawaem || isDashboardLightMode ? 'text-[#8b6914]' : 'text-violet-300',
  };

  const changeTypeTextClass = {
    create: qawaem || isDashboardLightMode ? 'text-[#5a855a]' : 'text-green-400',
    update: qawaem || isDashboardLightMode ? 'text-[#6e9b6e]' : 'text-blue-300',
    delete: qawaem || isDashboardLightMode ? 'text-red-600' : 'text-red-400',
  };

  const renderEntityType = (type) => (
    <span className={`text-xs font-medium ${entityTypeTextClass[type] || metaLabelClass}`}>
      {getEntityTypeLabel(type)}
    </span>
  );

  const renderChangeType = (type) => {
    if (!type) return null;
    return (
      <span className={`text-xs font-medium ${changeTypeTextClass[type] || metaLabelClass}`}>
        {getChangeTypeLabel(type)}
      </span>
    );
  };

  const renderValueChange = (record) => {
    if (!record.old_value && !record.new_value) return null;
    const arrow = p('value_arrow', { defaultValue: '←' });
    return (
      <span
        className={`text-xs truncate max-w-[220px] ${qawaem || isDashboardLightMode ? 'text-console-muted' : 'text-white/60'}`}
        title={`${record.old_value || p('empty_dash')} ${arrow} ${record.new_value || p('empty_dash')}`}
        dir="ltr"
      >
        {record.old_value || p('empty_dash')} {arrow} {record.new_value || p('empty_dash')}
      </span>
    );
  };

  const getStatusBadge = (status) => (
    <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${getCatalogStatusBadgeStyle(status)}`}>
      {isArticleActiveStatus(status) ? <CheckCircle size={14} /> : <XCircle size={14} />}
      {getArticleStatusLabel(status, t)}
    </span>
  );

  const selectProps = { searchable: true, creatable: true, autoSelect: false };

  return (
    <>
      <ArticlesConfigNotice
        message={!articlesConfig.enableChangelog ? p('notice_changelog_disabled', { defaultValue: 'سجل التغييرات معطّل — فعّله من إعدادات الأصناف ← السجل والعرض.' }) : null}
      />
      <FacturationListPageLayout
        className="space-y-6"
        containerClassName="employees-container"
        title={p('title')}
        subtitle={p('subtitle')}
        icon={History}
        stats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={buildBulkDeleteFilterAction({
          count: selectedRecords.length,
          onClick: handleBulkDelete,
          label: p('delete_selected', { count: selectedRecords.length }),
          title: p('delete_selected', { count: selectedRecords.length }),
        })}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder')}
        onCreate={openNewEntryModal}
        createLabel={p('new_item')}
        importExport={{
          importOpen: showImportDropdown,
          exportOpen: showExportDropdown,
          onToggleImport: () => {
            setShowImportDropdown(!showImportDropdown);
            setShowExportDropdown(false);
          },
          onToggleExport: () => {
            setShowExportDropdown(!showExportDropdown);
            setShowImportDropdown(false);
          },
          importRef: importDropdownRef,
          exportRef: exportDropdownRef,
          importLabel: p('import'),
          exportLabel: p('export'),
          onImportCSV: handleImportCSV,
          onImportExcel: handleImportExcel,
          onImportJSON: handleImportJSON,
          onExportCSV: handleExportCSV,
          onExportExcel: handleExportExcel,
          onExportJSON: handleExportJSON,
        }}
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={t('common.advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
        controlsStrip={
          !qawaem ? (
            <div className="facturation-list-controls-strip p-4 border-b border-white/10">
              <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
                {shouldShowCardsToolbarSearch(viewMode) ? (
                  <div className="relative min-w-0 flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                    <input
                      type="text"
                      placeholder={p('search_placeholder')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ) : null}
                <FacturationViewModeToggle
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  isLight={isDashboardLightMode}
                  cardsTitle={t('common.view_cards')}
                  tableTitle={t('common.view_table')}
                />
              </div>
            </div>
          ) : null
        }
      >
        {loading && (
          <div className="p-8 text-center text-white/60">{t('common.loading')}</div>
        )}

        {viewMode === 'table' && !loading && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                  />
                  {showCol('change_number') ? (
                    <TableColumnHeaderFilter
                      columnKey="change_number"
                      label={p('col_change_number')}
                      value={colFilters.change_number ?? ''}
                      onChange={(v) => setColFilter('change_number', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                    />
                  ) : null}
                  {showCol('entity_name') ? (
                    <TableColumnHeaderFilter
                      columnKey="entity_name"
                      label={p('col_entity')}
                      value={colFilters.entity_name ?? ''}
                      onChange={(v) => setColFilter('entity_name', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                    />
                  ) : null}
                  {showCol('field_name') ? (
                    <TableColumnHeaderFilter
                      columnKey="field_name"
                      label={p('col_field')}
                      value={colFilters.field_name ?? ''}
                      onChange={(v) => setColFilter('field_name', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                    />
                  ) : null}
                  {showCol('change_type') ? (
                    <TableColumnHeaderFilter
                      columnKey="change_type"
                      label={p('col_change')}
                      value={colFilters.change_type ?? ''}
                      onChange={(v) => setColFilter('change_type', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                    />
                  ) : null}
                  {showCol('user_name') ? (
                    <TableColumnHeaderFilter
                      columnKey="user_name"
                      label={p('col_user')}
                      value={colFilters.user_name ?? ''}
                      onChange={(v) => setColFilter('user_name', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                    />
                  ) : null}
                  {showCol('changed_at') ? (
                    <TableColumnHeaderFilter
                      columnKey="changed_at"
                      label={p('col_date')}
                      value={colFilters.changed_at ?? ''}
                      onChange={(v) => setColFilter('changed_at', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                    />
                  ) : null}
                  {showCol('status') ? (
                    <TableColumnHeaderFilter
                      columnKey="status"
                      label={p('col_status')}
                      kind="select"
                      options={statusColumnFilterOptions}
                      value={colFilters.status ?? ''}
                      onChange={(v) => setColFilter('status', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      align="center"
                      thClassName="text-center"
                    />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemChangelogColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleChangelogColumnCount}
                    totalCount={qawaemChangelogColumns.length}
                    thClassName={QAWAEM_TABLE_ACTIONS_TH_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {displayedEntries.length > 0 ? displayedEntries.map((record, index) => (
                  <tr
                    key={record.id}
                    className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}
                  >
                    <QawaemTableSelectRowCell
                      checked={selectedRecords.includes(record.id)}
                      onChange={() => handleSelectRecord(record.id)}
                    />
                    {showCol('change_number') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold font-mono">{record.change_number || '—'}</span>
                          <span className="text-white/50 text-xs mt-0.5">{record.id}</span>
                        </div>
                      </td>
                    ) : null}
                    {showCol('entity_name') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-0.5">
                          {renderEntityType(record.entity_type)}
                          <span className="text-white font-semibold">{record.entity_name || '—'}</span>
                          {record.entity_id ? (
                            <span className={`text-xs font-mono ${qawaem ? 'text-console-muted' : 'text-white/50'}`}>
                              {record.entity_id}
                            </span>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                    {showCol('field_name') ? (
                      <td className="px-6 py-4 text-white font-medium">{record.field_name || '—'}</td>
                    ) : null}
                    {showCol('change_type') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-0.5">
                          {renderChangeType(record.change_type)}
                          {renderValueChange(record)}
                        </div>
                      </td>
                    ) : null}
                    {showCol('user_name') ? (
                      <td className="px-6 py-4 text-white">{record.user_name || '—'}</td>
                    ) : null}
                    {showCol('changed_at') ? (
                      <td className="px-6 py-4 text-white/80 text-sm">{formatDate(record.changed_at)}</td>
                    ) : null}
                    {showCol('status') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge
                            status={record.status}
                            recordId={record.id}
                            options={STATUS_OPTIONS}
                            onChange={handleStatusChange}
                            title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })}
                          />
                        </div>
                      </td>
                    ) : null}
                    <QawaemTableStickyActionsTd>
                      <QawaemErpRowActions
                        onView={() => handleView(record)}
                        onEdit={() => handleEdit(record)}
                        onDelete={() => handleDelete(record.id)}
                        viewTitle={t('common.view')}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </QawaemTableStickyActionsTd>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={tableColSpan} className="text-center py-12 text-white/60">
                      <History size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_title')}</p>
                      <p className="text-sm text-white/40 mt-2">{p('empty_hint')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && !loading && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 rounded-b-3xl">
            {displayedEntries.length > 0 ? displayedEntries.map((record, index) => (
              <motion.div
                key={record.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mb-4">
                      <h3 className="text-xl font-bold text-white flex items-center gap-2 font-mono">
                        <Hash size={18} className="text-blue-400" />
                        {record.change_number || '—'}
                      </h3>
                      {renderEntityType(record.entity_type)}
                      {renderChangeType(record.change_type)}
                      <QawaemClickableStatusBadge
                        status={record.status}
                        recordId={record.id}
                        options={STATUS_OPTIONS}
                        onChange={handleStatusChange}
                        title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <h5 className="font-medium text-white mb-2">{p('section_entity')}</h5>
                        <div className="space-y-1 text-sm text-white/70">
                          <div><span className="font-medium">{p('field_entity_type')}:</span> {getEntityTypeLabel(record.entity_type)}</div>
                          <div><span className="font-medium">{p('field_entity_name')}:</span> {record.entity_name || '—'}</div>
                          {record.entity_id && <div><span className="font-medium">{p('field_entity_id')}:</span> {record.entity_id}</div>}
                        </div>
                      </div>
                      <div>
                        <h5 className="font-medium text-white mb-2">{p('section_change')}</h5>
                        <div className="space-y-1 text-sm text-white/70">
                          <div><span className="font-medium">{p('field_field_name')}:</span> {record.field_name || p('empty_dash')}</div>
                          {record.change_type && (
                            <div><span className="font-medium">{p('field_change_type')}:</span> {getChangeTypeLabel(record.change_type)}</div>
                          )}
                          <div><span className="font-medium">{p('field_old_value')}:</span> {record.old_value || p('empty_dash')}</div>
                          <div><span className="font-medium">{p('field_new_value')}:</span> {record.new_value || p('empty_dash')}</div>
                        </div>
                      </div>
                      <div>
                        <h5 className="font-medium text-white mb-2">{p('section_meta')}</h5>
                        <div className="space-y-1 text-sm text-white/70">
                          <div className="flex items-center gap-2">
                            <User size={14} className="text-cyan-400 shrink-0" />
                            <span><span className="font-medium">{p('field_user_name')}:</span> {record.user_name || p('empty_dash')}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar size={14} className="text-amber-400 shrink-0" />
                            <span><span className="font-medium">{p('field_changed_at')}:</span> {formatDate(record.changed_at)}</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h5 className="font-medium text-white mb-2">{p('section_notes')}</h5>
                        <p className="text-white/60 text-xs">{record.notes || '—'}</p>
                      </div>
                    </div>
                  </div>
                  <QawaemErpRowActions
                    compact
                    size={14}
                    onView={() => handleView(record)}
                    onEdit={() => handleEdit(record)}
                    onDelete={() => handleDelete(record.id)}
                    viewTitle={t('common.view')}
                    editTitle={t('common.edit')}
                    deleteTitle={t('common.delete')}
                  />
                </div>
              </motion.div>
            )) : (
              <div className="col-span-full text-center py-12">
                <History size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_title')}</p>
              </div>
            )}
          </div>
        )}

      </FacturationListPageLayout>

      {/* Add/Edit Modal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <History className="h-6 w-6 text-blue-400" />
                {selectedRecord ? p('modal_edit') : p('modal_new')}
              </h2>
              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  form="changelog-form"
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
                >
                  <CheckCircle size={18} />
                  {selectedRecord ? t('common.save') : t('common.create')}
                </button>
                <button type="button" onClick={resetForm} className="text-white/70 hover:text-white transition-colors">
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="shrink-0 px-6 pt-3 border-b border-white/10">
              <div className="flex flex-wrap gap-1" role="tablist" aria-label={selectedRecord ? p('modal_edit') : p('modal_new')}>
                {formTabs.map((tab) => {
                  const TabIcon = tab.icon;
                  const active = formTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setFormTab(tab.id)}
                      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${
                        active
                          ? 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px'
                          : 'text-white/55 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <TabIcon size={16} className={active ? (tab.id === 'change' ? 'text-blue-400' : tab.id === 'meta' ? 'text-purple-400' : tab.id === 'notes' ? 'text-amber-400' : 'text-emerald-400') : ''} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="changelog-form" onSubmit={handleSubmit}>
                {formTab === 'entity' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_change_number')}</label>
                      <input
                        type="text"
                        value={formData.change_number}
                        onChange={(e) => setFormData({ ...formData, change_number: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                        placeholder={p('placeholder_change_number')}
                        dir="ltr"
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">
                        <RequiredLabelText text={p('field_entity_type')} required />
                      </label>
                      <CustomSelect
                        value={formData.entity_type}
                        onChange={(val) => setFormData({ ...formData, entity_type: val || '' })}
                        options={entityTypeOptions}
                        placeholder={p('placeholder_entity_type')}
                        searchable={false}
                        creatable={false}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_entity_name')}</label>
                      <input
                        type="text"
                        value={formData.entity_name}
                        onChange={(e) => setFormData({ ...formData, entity_name: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={p('placeholder_entity_name')}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_entity_id')}</label>
                      <input
                        type="text"
                        value={formData.entity_id}
                        onChange={(e) => setFormData({ ...formData, entity_id: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                        placeholder={p('placeholder_entity_id')}
                        dir="ltr"
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">
                        <RequiredLabelText text={p('field_field_name')} required />
                      </label>
                      <CustomSelect
                        value={formData.field_name}
                        onChange={(val) => setFormData({ ...formData, field_name: val || '' })}
                        options={fieldNameOptions}
                        placeholder={p('placeholder_field_name')}
                        searchable
                        creatable
                        autoSelect={false}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_change_type')}</label>
                      <CustomSelect
                        value={formData.change_type}
                        onChange={(val) => setFormData({ ...formData, change_type: val || '' })}
                        options={changeTypeOptions}
                        placeholder={p('placeholder_change_type')}
                        searchable={false}
                        creatable={false}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_old_value')}</label>
                      <textarea
                        value={formData.old_value}
                        onChange={(e) => setFormData({ ...formData, old_value: e.target.value })}
                        rows={2}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={p('placeholder_old_value')}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_new_value')}</label>
                      <textarea
                        value={formData.new_value}
                        onChange={(e) => setFormData({ ...formData, new_value: e.target.value })}
                        rows={2}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={p('placeholder_new_value')}
                      />
                    </div>
                  </div>
                )}

                {formTab === 'meta' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_user_name')}</label>
                      <input
                        type="text"
                        value={formData.user_name}
                        onChange={(e) => setFormData({ ...formData, user_name: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={p('placeholder_user_name')}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_changed_at')}</label>
                      <CustomDatePicker
                        value={formData.changed_at ? formData.changed_at.slice(0, 10) : ''}
                        onChange={(v) => setFormData({ ...formData, changed_at: v ? new Date(v).toISOString() : '' })}
                        placeholder={p('placeholder_changed_at')}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">
                        <RequiredLabelText text={p('field_status')} required />
                      </label>
                      <CustomSelect
                        value={formData.status}
                        onChange={(val) => setFormData({ ...formData, status: val || '' })}
                        options={buildArticleStatusSelectOptions(t)}
                        placeholder={p('placeholder_status')}
                        searchable={false}
                        creatable={false}
                      />
                    </div>
                    <div className="min-w-0 md:col-span-3">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_notes')}</label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        rows={3}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={p('placeholder_notes')}
                      />
                    </div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* View Modal */}
      {showViewModal && selectedRecord && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowViewModal(false)} role="presentation">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <History className="h-6 w-6 text-blue-400" />
                {selectedRecord.change_number || p('col_change_number')}
              </h2>
              <button type="button" onClick={() => setShowViewModal(false)} className="text-white/60 hover:text-white transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                  <Package size={18} className="text-emerald-400" />
                  <h3 className="text-lg font-semibold text-white">{p('section_entity')}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><p className="text-white/60 text-xs uppercase mb-1">{p('col_change_number')}</p><p className="text-white font-semibold font-mono">{selectedRecord.change_number || '—'}</p></div>
                  <div><p className="text-white/60 text-xs uppercase mb-1">{p('field_entity_type')}</p><p className="font-medium">{renderEntityType(selectedRecord.entity_type)}</p></div>
                  <div><p className="text-white/60 text-xs uppercase mb-1">{p('field_entity_name')}</p><p className="text-white font-semibold">{selectedRecord.entity_name || '—'}</p></div>
                  <div><p className="text-white/60 text-xs uppercase mb-1">{p('field_entity_id')}</p><p className="text-white font-mono">{selectedRecord.entity_id || p('empty_dash')}</p></div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <GitCompare size={18} className="text-blue-400" />
                    <h3 className="text-lg font-semibold text-white">{p('section_change')}</h3>
                  </div>
                  <div className="space-y-3 text-white/80">
                    <div><span className="text-white/60 text-sm">{p('field_field_name')}:</span> <span className="text-white font-semibold ml-2">{selectedRecord.field_name || '—'}</span></div>
                    <div><span className="text-white/60 text-sm">{p('field_change_type')}:</span> <span className="ml-2">{renderChangeType(selectedRecord.change_type) || '—'}</span></div>
                    <div><span className="text-white/60 text-sm">{p('field_old_value')}:</span> <span className="text-white ml-2">{selectedRecord.old_value || '—'}</span></div>
                    <div><span className="text-white/60 text-sm">{p('field_new_value')}:</span> <span className="text-white ml-2">{selectedRecord.new_value || '—'}</span></div>
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <User size={18} className="text-purple-400" />
                    <h3 className="text-lg font-semibold text-white">{p('section_meta')}</h3>
                  </div>
                  <div className="space-y-3 text-white/80">
                    <div><span className="text-white/60 text-sm">{p('field_user_name')}:</span> <span className="text-white font-semibold ml-2">{selectedRecord.user_name || '—'}</span></div>
                    <div><span className="text-white/60 text-sm">{p('field_changed_at')}:</span> <span className="text-white ml-2">{formatDate(selectedRecord.changed_at)}</span></div>
                    <div><span className="text-white/60 text-sm">{p('field_status')}:</span> <span className="ml-2">{getStatusBadge(selectedRecord.status)}</span></div>
                  </div>
                </div>
              </div>

              {selectedRecord.notes && (
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <FileText size={18} className="text-amber-400" />
                    <h3 className="text-lg font-semibold text-white">{p('field_notes')}</h3>
                  </div>
                  <p className="text-white/70 text-sm italic">{selectedRecord.notes}</p>
                </div>
              )}
            </div>

            <button type="button" onClick={() => setShowViewModal(false)} className="w-full mt-6 bg-white/5 hover:bg-white/10 text-white py-3 rounded-xl font-semibold transition-all border border-white/10">
              {t('common.close')}
            </button>
          </motion.div>
        </div>,
        document.body
      )}
            <AppConfirmDialog
        open={deleteConfirm.open}
        title={t('common.delete', 'حذف')}
        message={
          deleteConfirm.ids.length > 1
            ? p('bulk_delete_confirm', { count: deleteConfirm.ids.length })
            : p('delete_confirm')
        }
        confirmLabel={t('common.delete', 'حذف')}
        cancelLabel={t('common.cancel', 'إلغاء')}
        danger
        onCancel={closeDeleteConfirm}
        onConfirm={executeDeleteConfirmed}
      />
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default ArticleChangelogPage;

